import { UserIdentity } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useCallback, useEffect, useState } from 'react';
import { keycloakAuthApiRef } from '../apis';

/**
 * Fully custom sign-in page — no SignInPage, no MUI card, no Backstage chrome.
 *
 * Replicates the exact logic from @backstage/core-components SignInPage.esm.js:
 *   1. On mount: check for an existing session (handles the OAuth redirect return).
 *   2. On button click: trigger the OAuth popup/redirect (instantPopup: true).
 *   3. On success: call props.onSignInSuccess(UserIdentity.create(...)).
 *
 * All styling is pure inline — zero dependency on MUI, JSS, or Backstage theming.
 */
export const CustomSignInPage = (props: any) => {
  const authApi = useApi(keycloakAuthApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<Error | null>(null);

  const signIn = useCallback(
    async (options: { instantPopup: boolean }) => {
      const identityResponse = await authApi.getBackstageIdentity(
        options.instantPopup ? { instantPopup: true } : { optional: true },
      );
      if (!identityResponse) return; // no existing session — stay on page

      const profile = await authApi.getProfile();
      props.onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authApi],
  );

  // Check for an existing session on mount — this fires automatically when
  // Keycloak redirects back to the app after a successful authentication.
  useEffect(() => {
    signIn({ instantPopup: false }).catch(() => {/* no existing session, do nothing */});
  }, [signIn]);

  const handleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signIn({ instantPopup: true });
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }
  };

  const provider = (props.providers as any[] | undefined)?.[0];
  const title    = provider?.title   ?? 'Login with SSO';
  const message  = provider?.message ?? 'Login with VW Group Cloud IDP';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0f1623',
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {/* Self-contained font load — no dependency on customStyles.css */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700&display=swap"
      />

      {/* Card hover — spring lift + deepened shadow. Pure CSS, no MUI/JSS dependency. */}
      <style>{`
        .sheriff-login-card {
          transition:
            transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
            box-shadow 0.3s ease;
        }
        .sheriff-login-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 56px rgba(0, 0, 0, 0.60) !important;
        }
      `}</style>

      <img
        src="/logo_vwtech_.png"
        alt="VWtech"
        style={{ width: 200, marginBottom: 20, display: 'block' }}
      />

      <div
        className="sheriff-login-card"
        style={{
          width: 420,
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
        }}
      >
        {/* Header — VW Blue 600 */}
        <div
          style={{
            backgroundColor: '#001E50',
            padding: '28px 32px',
            textAlign: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <span style={{ color: '#ffffff', fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {title}
          </span>
        </div>

        {/* Body — VW DigitalCobalt 100 */}
        <div
          style={{
            backgroundColor: '#11192E',
            padding: '28px 32px 36px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <span style={{ color: 'rgba(255, 255, 255, 0.82)', fontSize: 15, lineHeight: 1.5 }}>
            {message}
          </span>

          {error && (
            <span style={{ color: '#FF335C', fontSize: 13, lineHeight: 1.4 }}>
              {error.message}
            </span>
          )}

          <button
            type="button"
            disabled={loading}
            onClick={handleSignIn}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F2F4F7'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#ffffff'; }}
            style={{
              backgroundColor: '#ffffff',
              color: '#001E50',
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 700,
              padding: '10px 40px',
              borderRadius: 999,
              border: 'none',
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.7 : 1,
              letterSpacing: '0.01em',
            }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </div>
      </div>
    </div>
  );
};
